import React from 'react';
import {useMatches} from '@remix-run/react';
import type {I18nLocale} from '~/lib/type';

/**
 * "品牌故事 / OUR STORY" timeline for the About Us page — replaces the old
 * wall-of-text business list (頭盔王 / RENTAL819 / 26KING / RentalBike.HK)
 * with a vertical milestone timeline. Content is finalized and hardcoded per
 * locale (the About Us Sanity page has no structured module for this).
 *
 * Colours come from theme tokens, not hex literals, so the timeline tracks the
 * page it sits on: hk-orange is the same accent as the page heading's underline
 * in the pages route. Note that this config replaces Tailwind's `gray` scale
 * with a single flat colour, so `gray-200`/`gray-600` compile to nothing —
 * the muted tones here use hk-deep-gray and darkGray instead.
 */

interface Milestone {
  year: string;
  title: string;
  body: string;
}

const MILESTONES: Record<'zh' | 'en', Milestone[]> = {
  zh: [
    {
      year: '2014 年成立',
      title: '頭盔王',
      body: '香港大型電單車用品網上購物與實體店平台；多個國際品牌指定香港區代理。由頭盔、騎士裝備、電單車改裝部件等產品一應俱全，款色數量多達數以萬計。',
    },
    {
      year: '2017 年成立',
      title: 'RENTAL819 香港',
      body: '日本最大電單車租賃公司 Rental819 的指定香港及澳門區代理。我們為香港／澳門車友提供一條龍式租車、自駕遊旅行團、租車住宿自由行套票等服務。',
    },
    {
      year: '2023 年成立',
      title: '26KING',
      body: '26KING 為頭盔王附屬公司，除了為日歐品牌官方指定全新行貨車經銷商，另設資深車房團隊，提供星級電單車保養維修及改裝服務。由維修、改裝到車輛買賣，服務一應俱全！',
    },
    {
      year: '2024 年成立',
      title: 'RentalBike.HK',
      body: '全港唯一合法旅客電單車租賃平台。專為訪港旅客及本地車手而設，全車隊均為 2024—2026 年最新型號，並提供 24/7 路面支援與 Premium Go CDW 保障，讓你以最安心方式探索香港。',
    },
  ],
  en: [
    {
      year: 'Est. 2014',
      title: 'Helmetking',
      body: "Hong Kong's large-scale online and physical store for motorcycle gear, and the appointed HK distributor for many international brands. Helmets, riding gear and custom parts — tens of thousands of options in stock.",
    },
    {
      year: 'Est. 2017',
      title: 'RENTAL819 Hong Kong',
      body: 'Appointed HK & Macau agent for Rental819, Japan’s largest motorcycle rental company. We offer HK/Macau riders end-to-end rentals, self-drive tours, and rent-and-stay travel packages.',
    },
    {
      year: 'Est. 2023',
      title: '26KING',
      body: 'A Helmetking subsidiary and the officially appointed dealer for Japanese and European brand-new bikes. Its veteran workshop team delivers premium servicing, maintenance and custom work — from repairs to buying and selling.',
    },
    {
      year: 'Est. 2024',
      title: 'RentalBike.HK',
      body: "Hong Kong's only licensed motorcycle rental platform for visitors. Built for tourists and local riders alike, the whole fleet is 2024–2026 latest models, with 24/7 roadside support and Premium Go CDW cover so you can explore Hong Kong with total peace of mind.",
    },
  ],
};

export default function BrandStory() {
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale as I18nLocale | undefined;
  const lang = selectedLocale?.language === 'EN' ? 'en' : 'zh';
  const milestones = MILESTONES[lang];
  const kicker = lang === 'en' ? 'OUR STORY' : 'OUR STORY';
  const heading = lang === 'en' ? 'Our Story' : '品牌故事';

  return (
    <section className="mx-auto max-w-3xl px-1 pb-10">
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hk-orange">
          {kicker}
        </p>
        <h2 className="mt-2 text-3xl font-bold lg:text-4xl">{heading}</h2>
      </div>

      <ol className="relative ml-3">
        {/* connecting line */}
        <span
          aria-hidden
          className="absolute left-[7px] top-2 bottom-2 w-px bg-hk-deep-gray"
        />
        {milestones.map((m, i) => (
          <li key={m.title} className={i === milestones.length - 1 ? 'pl-8' : 'pl-8 pb-12'}>
            {/* dot */}
            <span
              aria-hidden
              className="absolute left-0 mt-1 flex h-[15px] w-[15px] items-center justify-center rounded-full bg-hk-orange"
            >
              <span className="h-[5px] w-[5px] rounded-full bg-white" />
            </span>
            <p className="text-sm font-bold text-hk-orange">{m.year}</p>
            <h3 className="mt-1 text-2xl font-bold tracking-wide">{m.title}</h3>
            <p className="mt-3 leading-relaxed text-darkGray">{m.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
